import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import {
  AUTH_COLORS,
  AUTH_CONTACT,
  AUTH_FONT,
  resolveQuickHelpContact,
} from "../../config/authConstants";
import { logoutSuccess, selectUser } from "../../redux/modules/userSlice";
import { performLogout } from "../api";
import { useUnsavedGuard } from "../Joinee/UnsavedChanges";
import spanideaLogo from "../../Assests/Images/auth/spanidea-logo.svg";
import iconPhone from "../../Assests/Images/auth/icon-phone.svg";
import Uploadphotoicon from "../../Assests/Images/uploadphotoicon.png";

function AuthHeader(props) {
  var showLogout = Boolean(props.showLogout);
  var dispatch = useDispatch();
  var navigate = useNavigate();
  var [anchorEl, setAnchorEl] = useState(null);
  const { user } = useSelector(selectUser);
  var quickHelp = resolveQuickHelpContact(user);
  var hasHrPhone = quickHelp.phoneHref !== AUTH_CONTACT.phoneHref;
  var guard = useUnsavedGuard();

  function handleLogoClick(event) {
    if (!showLogout) return;
    event.preventDefault();
    guard(function () {
      navigate("/");
    });
  }

  function handleLogout() {
    setAnchorEl(null);
    guard(doLogout);
  }

  function doLogout() {
    performLogout().then(
      function () {
        dispatch(logoutSuccess());
        navigate("/login");
      },
      function () {
        dispatch(logoutSuccess());
        navigate("/login");
      }
    );
  }

  function handleMenuOpen(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleMenuClose() {
    setAnchorEl(null);
  }

  function handleProfileClick() {
    handleMenuClose();
    navigate("/");
  }

  var avatarSrc =
    user && user.profilePhoto ? user.profilePhoto : Uploadphotoicon;

  return (
    <AppBar
      position="static"
      elevation={0}
      color="transparent"
      sx={{
        bgcolor: AUTH_COLORS.surface,
        color: AUTH_COLORS.text,
        borderBottom: "1px solid " + AUTH_COLORS.border,
        zIndex: 2,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: { xs: 48, sm: 44 },
          px: { xs: 2, sm: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Box
          component={RouterLink}
          to={showLogout ? "/" : "/login"}
          onClick={handleLogoClick}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            lineHeight: 0,
            textDecoration: "none",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={spanideaLogo}
            alt="SPANIDEA"
            sx={{
              width: { xs: 130, sm: 159.444 },
              height: { xs: 16, sm: 20 },
              display: "block",
              objectFit: "contain",
            }}
          />
        </Box>

        {showLogout ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexShrink: 0,
              ml: "auto",
            }}
          >
            <Tooltip title={hasHrPhone ? "Call HR" : ""} arrow>
            <Link
              component={hasHrPhone ? "a" : "span"}
              href={hasHrPhone ? quickHelp.phoneHref : undefined}
              underline="none"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                color: "inherit",
                flexShrink: 0,
                cursor: hasHrPhone ? "pointer" : "default",
              }}
            >
              <Typography
                component="span"
                sx={{
                  display: { xs: "none", sm: "inline" },
                  fontFamily: AUTH_FONT,
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: AUTH_COLORS.textStrong,
                  whiteSpace: "nowrap",
                }}
              >
                {AUTH_CONTACT.quickHelpLabel}
              </Typography>

              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    border: "1px solid " + AUTH_COLORS.borderSecondary,
                    bgcolor: AUTH_COLORS.surface,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={iconPhone}
                    alt=""
                    sx={{ width: 15, height: 15, display: "block" }}
                  />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: AUTH_FONT,
                    fontSize: { xs: 14, sm: 16 },
                    fontWeight: 600,
                    lineHeight: 1.2,
                    color: AUTH_COLORS.text,
                    whiteSpace: "nowrap",
                  }}
                >
                  {quickHelp.phoneDisplay}
                </Typography>
              </Box>
            </Link>
            </Tooltip>

            <IconButton
              onClick={handleMenuOpen}
              size="small"
              aria-label="User menu"
              sx={{ p: 0.25 }}
            >
              <Avatar
                src={avatarSrc}
                alt={user && user.name ? user.name : "User"}
                sx={{
                  width: 30,
                  height: 30,
                  border: "1px solid " + AUTH_COLORS.borderSecondary,
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 150,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  borderRadius: "8px",
                  "& .MuiMenuItem-root": {
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontFamily: AUTH_FONT,
                    "&:hover": {
                      backgroundColor: AUTH_COLORS.bgSoft,
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        ) : null}
      </Toolbar>
    </AppBar>
  );
}

export default AuthHeader;
